import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import {
  Loader2, WifiOff, RefreshCw, ListChecks, Bug, LayoutDashboard, Puzzle, Bot,
  Check, X, Clock, DollarSign, Globe, Monitor, Repeat, ExternalLink,
  FolderKanban, ChevronRight, ChevronDown, Inbox, Plus, CheckCircle2,
  RotateCw, AlertCircle, FileText, FileSpreadsheet,
  Download, Search, ArrowLeft, Boxes,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import DataTable from "@/components/dashboard/DataTable";
import Modal from "@/components/dashboard/Modal";
import Button from "@/components/ui/Button";
import { StatusBadge, SeverityTag } from "@/components/ui/Badge";
import StepTimeline from "@/components/shared/StepTimeline";
import BlockedDiagnosis from "@/components/shared/BlockedDiagnosis";
import { normalizeSteps } from "@/lib/runSteps";
import { blockedReasonOfRun, blockedReasonOfItem } from "@/lib/blockedDiagnosis";
import EmptyState from "@/components/shared/EmptyState";
import { cn } from "@/lib/cn";
import { runCost } from "@/lib/flows";
import { toastError, toastSuccess } from "@/lib/toast";
import { pollUntil } from "@/lib/poll";
import { useApiData } from "@/hooks/useApiData";
import { useAuth } from "@/context/AuthContext";
import { startRun, getRun, isTerminal, downloadRunReport, listTestRuns, listBugs } from "@/api/runs";
import { getExtRun, listExtRuns, listExtBatches, getExtBatch, getExtBatchItems, downloadExtBatchReport } from "@/api/extension";
import { getExeRun, listExeRuns, listExeBatches, getExeBatch, listExeBatchItems, downloadExeBatchReport } from "@/api/exe";
import { getProjects } from "@/api/projects";
import { DEMO_PROJECTS } from "@/data/projectsMock";
import { formatDateTime } from "@/lib/dates";
import { formatUsd } from "@/lib/format";

// ---------------------------------------------------------------------------
// The three testing flows — mirrors the company Runs & Bugs page, but every
// data source is scoped to the CALLER's own runs (mine=true / /test/bugs?mine)
// so a tester sees only the cases they tested. This page is shared with the PM
// shell (/pm/runs), where the caller is company-wide in the backend
// (core/access.py: COMPANY_WIDE_ROLES includes project_manager), so the `mine`
// flag is dropped and the SAME endpoints return the whole company's work.
// ---------------------------------------------------------------------------
const now = Date.now();
const iso = (hAgo) => new Date(now - hAgo * 3600000).toISOString();

// There are deliberately no DEMO_STEPS / DEMO_BUG fixtures here. The run-detail
// modal used to fall back to them when its fetch failed, rendering invented
// passing steps and an invented bug report under the real headings. Evidence on a
// review surface must come from the run or not be shown at all.

const DASH_DEMO_RUNS = [
  { id: "d1", status: "passed", url: "https://acme.com/login", instruction: "Login happy path", steps_passed: 5, steps_failed: 0, duration_seconds: 12.3, created_at: iso(2) },
  { id: "d2", status: "failed", url: "https://acme.com/cart", instruction: "Checkout with an empty cart", steps_passed: 2, steps_failed: 1, duration_seconds: 8.1, created_at: iso(6) },
  { id: "d3", status: "error", url: "https://acme.com/pay", instruction: "Pay with a test card", steps_passed: 3, steps_failed: 1, duration_seconds: 21.7, created_at: iso(28) },
];

const FLOWS = {
  dashboard: {
    key: "dashboard", label: "Tester Dashboard", icon: LayoutDashboard,
    blurb: "Cloud runs launched by testers from the web app.",
    detailApi: getRun,
    // Cloud/headless dashboard testing is paused — this source is shown but inert.
    disabled: true,
  },
  extension: {
    key: "extension", label: "Extension Testing", icon: Puzzle,
    blurb: "Headed runs driven live in a tester's real Chrome tab.",
    detailApi: getExtRun,
    // Batch-first: a batch groups the cases run together in one go.
    // mine follows the caller's role (see RunsBugs): true for testers, so the
    // home flow card and this page report the SAME totals — a tester sees only
    // the work they did; false for company-wide roles (PM/admin/superadmin),
    // where the backend scopes these to the whole company.
    batchesApi: (mine) => listExtBatches(100, { mine }),
    // …but a Run Single run belongs to no batch, so it needs its own source or it
    // is invisible on this page entirely. standalone=true excludes batch runs, so
    // nothing is listed twice. mine must match the batches above: these rows sit
    // in the SAME list, so they must share one visibility rule.
    singleRunsApi: (mine) => listExtRuns(100, { mine, standalone: true }),
    batchApi: getExtBatch,
    batchItemsApi: getExtBatchItems,
    reportApi: downloadExtBatchReport,
  },
  exe: {
    key: "exe", label: "Exe Agent Testing", icon: Bot,
    blurb: "Runs executed on a tester's machine by the desktop agent.",
    detailApi: getExeRun,
    batchesApi: (mine) => listExeBatches(100, { mine }),
    singleRunsApi: (mine) => listExeRuns(100, { mine, standalone: true }),
    batchApi: getExeBatch,
    batchItemsApi: listExeBatchItems,
    reportApi: downloadExeBatchReport,
  },
};

// Disabled "Tester Dashboard" (cloud runs) sits last, after Exe Agent.
const FLOW_ORDER = ["extension", "exe", "dashboard"];
// Identifies the "Unscoped" pseudo-project in the expanded-sections Set — it has
// no row in `projects`, so Expand all used to leave it shut.
const UNSCOPED_KEY = "__unscoped__";
const STATUS_OPTIONS = ["passed", "failed", "error", "cancelled", "blocked"];
// Hoisted out of the render. DataTable takes these into the dependency arrays of
// its filter/sort memo and its selection ids, so an inline literal made the whole
// pipeline re-run on every keystroke anywhere in the tree.
const STATUS_FILTER = [{ key: "status", label: "Status", options: STATUS_OPTIONS }];
const BUG_FILTERS = [
  { key: "resolvedLabel", label: "State", options: ["Open", "Resolved"] },
  { key: "severity", label: "Severity", options: ["low", "medium", "high", "critical"] },
];
const CASE_SEARCH_KEYS = ["name", "instruction"];
const DASH_SEARCH_KEYS = ["instruction", "url", "title"];
const getRowId = (r) => r.id;

const fmtWhen = (v) => formatDateTime(v);
function fmtDur(s) { return s == null ? "—" : s < 60 ? `${Number(s).toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`; }
// runCost reads whichever cost key the runner wrote — the exe agent writes
// `cost_usd`, so reading `estimated_cost_usd` alone showed every exe run as free.
// A run that genuinely cost NOTHING is not a run whose cost is unknown. The old
// `c ? ... : null` collapsed both into "—", so every zero-cost replay — which is
// most of them, since a compiled replay calls no model — looked like missing data.
// formatUsd shows $0.00 for free and "—" only when there is no cost to read.
function costOf(r) {
  const usage = r.token_usage;
  if (usage === null || usage === undefined) return null;
  return formatUsd(runCost(usage));
}
function isFail(status) { return ["failed", "error"].includes(String(status).toLowerCase()); }

// Steps are normalized by lib/runSteps — the one place that knows how each
// runner spells the same facts. There used to be a local mapper here, and it
// defaulted a missing verdict to `"pass"`. The exe agent never writes `status`
// (it writes `success: true|false`), so EVERY step of a failed exe case rendered
// as a green tick under a "N passed" counter and a FAILED badge. It also read
// `target` alone (exe writes `selector`) and `screenshot_path` (which no runner
// writes at all — see the shotIndexes note below).
//
// Pass the RAW steps to StepTimeline: it normalizes internally, and normalizing
// twice loses `text` and `inputValue`, because the second pass looks for
// `reason`/`detail` on an object that now only has `text`.
// Normalize any flow's bug into a common shape (or null).
function normBug(d) {
  const b = d.bug || d.bug_report || null;
  if (!b) return null;
  return {
    title: b.title || "Issue found",
    severity: b.severity || "medium",
    description: b.description || "",
    expected: b.expected || "",
    actual: b.actual || "",
    steps_to_reproduce: b.steps_to_reproduce || b.repro || [],
  };
}

function MetaChip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-white/5 dark:text-slate-300">
      <Icon size={13} /> {children}
    </span>
  );
}

// Per-row PDF / Excel download for a single run. stopPropagation so it doesn't
// also open the row's detail modal.
//
// The download is AWAITED and its failure REPORTED. These used to be bare
// `downloadRunReport(...)` calls: the helper throws on any non-OK response, so a
// failure was an unhandled rejection — no toast, no spinner, no file, and
// nothing to tell it apart from a slow browser. The batch-report button on this
// same page already worked this way.
function ReportButtons({ runId }) {
  const [busy, setBusy] = useState(null); // "pdf" | "xlsx" | null

  const download = async (e, format) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(format);
    try {
      await downloadRunReport(runId, format);
    } catch (err) {
      toastError((err && err.message) || "That report couldn't be downloaded.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex items-center justify-end gap-1">
      <button
        type="button"
        title="Download this run's PDF report"
        onClick={(e) => download(e, "pdf")}
        disabled={!!busy}
        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-60 dark:text-slate-300 dark:hover:bg-white/10"
      >
        {busy === "pdf" ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} PDF
      </button>
      <button
        type="button"
        title="Download this run's Excel report"
        onClick={(e) => download(e, "xlsx")}
        disabled={!!busy}
        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-60 dark:text-slate-300 dark:hover:bg-white/10"
      >
        {busy === "xlsx" ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={13} />} Excel
      </button>
    </div>
  );
}

function StatPill({ icon: Icon, value, label, tone }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/70 bg-white/70 px-2.5 py-1 text-xs backdrop-blur dark:border-white/10 dark:bg-white/5">
      <Icon size={12} className={tone} />
      <span className="font-semibold text-slate-900 dark:text-white">{value}</span>
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Detail modal — fetches the full run for the active flow, then renders meta +
// step timeline + bug report. For the dashboard flow it also exposes the
// tester's Re-run + PDF/Excel actions in the footer.
// ---------------------------------------------------------------------------
function DetailModal({ flow, row, onClose, onRerun, rerunning }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(false); setDetail(null);
    FLOWS[flow].detailApi(row.id)
      .then((d) => { if (alive) setDetail(d); })
      .catch(() => { if (alive) setErr(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [flow, row.id]);

  const d = detail || row;
  // NO placeholder timeline. When the detail fetch fails this used to substitute
  // DEMO_STEPS/DEMO_BUG — three hardcoded PASSING steps ("Opened the login page"…)
  // rendered under the real "Step timeline" heading with a real "3 passed" counter.
  // A reviewer looking at a genuinely failed case was shown a fabricated passing
  // run. Evidence on a QA surface must never be invented; show the error instead.
  // Memoised so the `steps` memo below has a stable input — a bare `||` chain
  // builds a new [] on every render and defeats it.
  const rawSteps = useMemo(() => d.step_results || d.steps || [], [d.step_results, d.steps]);
  // Which step indexes actually have a screenshot on disk. NO runner writes a
  // `screenshot_path` back onto a step, so the exe and extension run payloads
  // report it as this list instead — without passing it down, the timeline could
  // never show a single thumbnail on either of the two reachable flows.
  const shotIndexes = useMemo(
    () => (Array.isArray(d.screenshots) ? d.screenshots.map(Number) : []),
    [d.screenshots]
  );
  // Normalized copy for the COUNTERS only; the timeline gets `rawSteps`.
  const steps = useMemo(() => normalizeSteps(rawSteps, shotIndexes), [rawSteps, shotIndexes]);
  const bug = normBug(d);
  const isBlocked = String(d.status || "").toLowerCase() === "blocked";
  const blockedReason = useMemo(() => (isBlocked ? blockedReasonOfRun(d) : ""), [isBlocked, d]);
  const accountable = useMemo(
    () => (isBlocked ? steps.filter((s) => s.action !== "done") : steps),
    [steps, isBlocked]
  );
  const passed = accountable.filter((s) => s.status === "PASS").length;
  const failed = accountable.filter((s) => s.status === "FAIL" || s.status === "ERROR").length;
  const executedSteps = accountable.filter((s) => s.action !== "done").length;
  // Steps the runner never gave a verdict for. Counting them silently as passes
  // is exactly the bug this replaces, so they get their own honest tally.
  const unreported = accountable.filter((s) => !s.status).length;
  const cost = costOf(d);

  const canRerun = flow === "dashboard" && d.url && d.instruction;

  return (
    <Modal
      open
      onClose={onClose}
      title="Run detail"
      size="xl"
      /* The footer is rendered for EVERY flow. It used to exist only for the
         dashboard flow — the one that is disabled — so on extension and exe, the
         two a tester can actually reach, the dialog had no Close button at all.
         The dashboard-only actions stay conditional inside it. */
      footer={
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex flex-wrap gap-1">
            {flow === "dashboard" && <ReportButtons runId={d.id} />}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
            {flow === "dashboard" && (
              <Button
                size="sm"
                onClick={() => onRerun && onRerun({ id: d.id, url: d.url, instruction: d.instruction, projectId: d.project_id })}
                disabled={rerunning || !canRerun}
                title="Queue a brand-new run with the same URL and instruction"
              >
                {rerunning ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />} Re-run
              </Button>
            )}
          </div>
        </div>
      }
    >
      {loading && !detail ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : (
        <div className="space-y-5">
          {err && (
            <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
              <WifiOff size={14} /> Couldn&apos;t load this run&apos;s detail. Nothing below is
              from the run itself — close and try again.
            </div>
          )}

          {/* meta */}
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={d.status} />
            {d.browser && <MetaChip icon={Monitor}>{d.browser}</MetaChip>}
            {d.is_replay && <MetaChip icon={Repeat}>replay</MetaChip>}
            <MetaChip icon={Clock}>{fmtDur(d.duration_seconds)}</MetaChip>
            {cost && <MetaChip icon={DollarSign}>{cost.replace("$", "")}</MetaChip>}
            <MetaChip icon={Clock}>{fmtWhen(d.created_at)}</MetaChip>
          </div>

          {/* Only rendered when there is something to render. On a failed detail
              fetch `d` falls back to the list row, which for extension/exe is
              just { id } — so this printed a pair of empty quotes above a link
              with no href. */}
          {(d.instruction || d.url) && (
            <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-white/5">
              {d.instruction && <p className="text-sm text-slate-700 dark:text-slate-200">“{d.instruction}”</p>}
              {d.url && (
                <a href={d.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-brand-600 hover:underline dark:text-brand-400">
                  <Globe size={12} /> {d.url} <ExternalLink size={11} />
                </a>
              )}
            </div>
          )}

          {isBlocked && (
            <BlockedDiagnosis
              reason={blockedReason}
              affected={executedSteps > 0
                ? `Execution stopped at the blocking point after ${executedSteps} step${executedSteps === 1 ? "" : "s"} ran; no result was produced.`
                : null}
              missingEvidence={shotIndexes.length === 0}
            />
          )}

          {/* step counters */}
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-lg bg-slate-100 px-2.5 py-1 font-medium text-slate-600 dark:bg-white/5 dark:text-slate-300">{steps.length} steps</span>
            <span className="rounded-lg bg-emerald-100 px-2.5 py-1 font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">{passed} passed</span>
            {failed > 0 && <span className="rounded-lg bg-rose-100 px-2.5 py-1 font-medium text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">{failed} failed</span>}
            {unreported > 0 && (
              <span title="The runner recorded these steps but reported no verdict for them."
                    className="rounded-lg bg-slate-100 px-2.5 py-1 font-medium text-slate-500 dark:bg-white/10 dark:text-slate-400">
                {unreported} not reported
              </span>
            )}
          </div>

          {/* bug report */}
          {bug && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50/70 p-4 dark:border-rose-500/20 dark:bg-rose-500/10">
              <div className="flex items-start justify-between gap-2">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-white"><Bug size={15} className="text-rose-500" /> {bug.title}</span>
                <SeverityTag severity={bug.severity} />
              </div>
              {bug.description && <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{bug.description}</p>}
              <dl className="mt-3 space-y-1.5 text-xs">
                {bug.expected && <div><dt className="inline font-semibold text-slate-500 dark:text-slate-400">Expected: </dt><dd className="inline text-slate-600 dark:text-slate-300">{bug.expected}</dd></div>}
                {bug.actual && <div><dt className="inline font-semibold text-slate-500 dark:text-slate-400">Actual: </dt><dd className="inline text-slate-600 dark:text-slate-300">{bug.actual}</dd></div>}
              </dl>
              {Array.isArray(bug.steps_to_reproduce) && bug.steps_to_reproduce.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Steps to reproduce</p>
                  <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-xs text-slate-600 dark:text-slate-300">
                    {bug.steps_to_reproduce.map((s, i) => <li key={i}>{typeof s === "string" ? s : s.text || JSON.stringify(s)}</li>)}
                  </ol>
                </div>
              )}
            </div>
          )}

          {/* timeline */}
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Step timeline</h4>
            {steps.length === 0
              ? (
                <p className="text-sm text-slate-400">
                  {err
                    ? "Couldn’t load the steps for this run."
                    : "No step detail recorded for this run."}
                </p>
              )
              /* runId + kind for EVERY flow. runId was passed only for "dashboard",
                 which is the one disabled flow — so on extension and exe, the two
                 reachable ones, screenshots could never load.
                 RAW steps, not the normalized copy above: StepTimeline normalizes
                 internally and a second pass drops the failure text. */
              : <StepTimeline steps={rawSteps} runId={d.id}
                              kind={flow === "dashboard" ? "cloud" : flow}
                              runStatus={d.status}
                              shotIndexes={shotIndexes} />}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Batch helpers — normalize the batch summary shape shared by ext + exe.
// ---------------------------------------------------------------------------
function normList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.batches)) return res.batches;
  if (Array.isArray(res?.items)) return res.items;
  return [];
}
function batchName(b) {
  return b.label || b.name || b.title || `Batch ${String(b.id ?? "").slice(0, 8)}`;
}
function batchStats(b) {
  const passed = b.passed || 0;
  const failed = (b.failed || 0) + (b.errored || 0);
  const inconclusive = b.inconclusive || 0;
  const cancelled = (b.cancelled || 0) + (b.orphaned || 0);
  const total = b.total || 0;
  const done = passed + failed + inconclusive + cancelled;
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return { passed, failed, inconclusive, cancelled, total, done, pct };
}
// A BUG is a case whose run produced an actual bug report — not merely one that
// failed. These were the same number, so the "Bugs" toggle always showed exactly
// the failed count and the two told the reader nothing different. `has_bug` comes
// from the batch-items endpoint; a payload without it (an older backend) falls
// back to the failed status so nothing silently reads zero after an upgrade.
function itemIsBug(i) {
  return i && i.has_bug === undefined ? isFail(i.status) : !!(i && i.has_bug);
}
// Same rule for a standalone run row, which carries `has_bug` on the run list.
function runIsBug(r) {
  return r && r.has_bug === undefined ? isFail(r.status) : !!(r && r.has_bug);
}

// ---------------------------------------------------------------------------
// HeadedFlowPanel — Extension / Exe, batch-first. Shows a list of batches;
// clicking one opens its cases (filterable); clicking a case opens the run
// detail (with screenshots) via the shared DetailModal.
// ---------------------------------------------------------------------------
// refreshNonce is threaded down as a prop rather than used to remount this panel
// from the parent: remounting reset `batchId`, so hitting Refresh while reading a
// batch bounced you back out to the batch list.
function HeadedFlowPanel({ flow, mine, onOpen, refreshNonce }) {
  const [batchId, setBatchId] = useState(null);
  if (batchId) {
    return <BatchDetail flow={flow} batchId={batchId} onBack={() => setBatchId(null)}
                        onOpenCase={onOpen} refreshNonce={refreshNonce} />;
  }
  return <BatchList flow={flow} mine={mine} onOpenBatch={setBatchId} onOpenRun={onOpen} refreshNonce={refreshNonce} />;
}

// ---- Single-run card ------------------------------------------------------
// A run started from Run Single has no batch to open, so the whole card opens
// its run detail (steps + screenshots + bug report) directly.
function SingleRunCard({ flow, run, onOpen }) {
  const Icon = FLOWS[flow].icon;
  const bug = runIsBug(run);
  const cost = costOf(run);
  return (
    <button
      type="button"
      onClick={() => onOpen(flow, { id: run.id })}
      className="card group flex w-full items-center gap-3 p-4 text-left transition-colors hover:border-brand-300 dark:hover:border-brand-500/40"
    >
      <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl",
        bug ? "bg-rose-500/10 text-rose-600 dark:text-rose-300" : "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300")}>
        {bug ? <Bug size={18} /> : <Icon size={18} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate font-semibold text-slate-900 group-hover:text-brand-600 dark:text-white dark:group-hover:text-brand-300">
            {run.instruction || run.url || `Run ${String(run.id).slice(0, 8)}`}
          </span>
          <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.625rem] font-medium text-slate-500 dark:bg-white/10 dark:text-slate-300">
            single run
          </span>
          {run.is_replay && (
            <span title="Replay of an earlier run" className="inline-flex shrink-0 items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.625rem] font-medium text-slate-500 dark:bg-white/10 dark:text-slate-300">
              <Repeat size={10} /> replay
            </span>
          )}
        </span>
        <span className="block truncate text-xs text-slate-400">
          {fmtWhen(run.created_at)}
          {run.duration_seconds != null ? ` · ${fmtDur(run.duration_seconds)}` : ""}
          {cost ? ` · ${cost}` : ""}
          {run.url ? ` · ${run.url}` : ""}
        </span>
      </span>
      <StatusBadge status={run.status} />
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-slate-400 transition-transform group-hover:translate-x-0.5">
        <ChevronRight size={16} />
      </span>
    </button>
  );
}

// ---- Batch list -----------------------------------------------------------
// Lists both batches AND single runs, newest first. Single runs used to be
// invisible here: this panel only ever read the batches endpoint, and a run
// started from Run Single has no batch item pointing at it.
function BatchList({ flow, mine, onOpenBatch, onOpenRun, refreshNonce }) {
  const cfg = FLOWS[flow];
  const [batches, setBatches] = useState([]);
  const [singles, setSingles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [reporting, setReporting] = useState(null); // batch id downloading
  const [reportError, setReportError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(false);
    // Batches and single runs are fetched independently: one source failing must
    // not blank the other, so each settles on its own.
    Promise.allSettled([cfg.batchesApi(mine), cfg.singleRunsApi(mine)])
      .then(([b, r]) => {
        if (!alive) return;
        if (b.status === "fulfilled") setBatches(normList(b.value)); else setError(true);
        if (r.status === "fulfilled") setSingles(normList(r.value)); else setError(true);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [cfg, mine, refreshNonce]);

  const totals = useMemo(() => {
    const t = batches.reduce((acc, b) => {
      const s = batchStats(b);
      acc.cases += s.total; acc.passed += s.passed; acc.failed += s.failed;
      return acc;
    }, { cases: 0, passed: 0, failed: 0 });
    // A single run is one case. Its failure counts toward Failed; whether it is
    // also a BUG is a separate question — see runIsBug, used by the row itself.
    for (const r of singles) {
      t.cases += 1;
      if (String(r.status).toLowerCase() === "passed") t.passed += 1;
      else if (isFail(r.status)) t.failed += 1;
    }
    return t;
  }, [batches, singles]);

  const statusOptions = useMemo(
    () => Array.from(new Set([
      "passed", "failed", "error", "cancelled", "blocked",
      ...[...batches, ...singles].map((e) => String(e.status || "").toLowerCase()).filter(Boolean),
    ])),
    [batches, singles]
  );

  // One chronological list of both kinds, so a single run sits where it happened
  // rather than in a separate section nobody scrolls to.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (text) => !q || String(text || "").toLowerCase().includes(q);
    const entries = [
      ...batches.map((b) => ({ kind: "batch", key: `b-${b.id}`, at: b.created_at, data: b })),
      ...singles.map((r) => ({ kind: "run", key: `r-${r.id}`, at: r.created_at, data: r })),
    ];
    return entries
      .filter((e) => {
        if (status !== "all" && String(e.data.status || "").toLowerCase() !== status) return false;
        if (e.kind === "batch") return matches(batchName(e.data)) || matches(e.data.id);
        return matches(e.data.instruction) || matches(e.data.url) || matches(e.data.id);
      })
      .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  }, [batches, singles, query, status]);

  const downloadReport = async (e, id) => {
    e.stopPropagation();
    setReporting(id); setReportError(null);
    try {
      await cfg.reportApi(id);
    } catch (err) {
      // A failed download used to do literally nothing visible — the spinner
      // stopped and no file arrived, with no way to tell that from a slow browser.
      setReportError((err && err.message) || "That report couldn't be downloaded.");
    } finally { setReporting(null); }
  };

  const statCards = [
    { label: "Batches", value: batches.length, icon: Boxes, cls: "text-brand-600 bg-brand-500/10 dark:text-brand-300" },
    { label: "Single runs", value: singles.length, icon: FLOWS[flow].icon, cls: "text-violet-600 bg-violet-500/10 dark:text-violet-300" },
    { label: "Cases", value: totals.cases, icon: ListChecks, cls: "text-sky-600 bg-sky-500/10 dark:text-sky-300" },
    { label: "Passed", value: totals.passed, icon: Check, cls: "text-emerald-600 bg-emerald-500/10 dark:text-emerald-300" },
    // "Failed", not "Failed / bugs": a batch summary reports pass/fail counts and
    // carries no bug-report count, so this tile could never have been the latter.
    // Per-case bug counts live in the batch's own Bugs toggle, which reads has_bug.
    { label: "Failed", value: totals.failed, icon: X, cls: "text-rose-600 bg-rose-500/10 dark:text-rose-300" },
  ];

  return (
    <div>
      {reportError && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-2.5 text-sm text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          <AlertCircle size={15} className="shrink-0" /> {reportError}
        </div>
      )}

      {/* Each list is capped at 100 with no pager, and these tiles are computed
          from exactly what was fetched — so on a busy company they described the
          newest 100 batches, not the company. Say so. */}
      {(batches.length >= 100 || singles.length >= 100) && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertCircle size={15} className="shrink-0" />
          Showing the 100 most recent batches and the 100 most recent single runs.
          The totals below cover only these.
        </div>
      )}

      {/* stat strip */}
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
        {statCards.map((s) => (
          <div key={s.label} className="card flex items-center gap-3 p-4">
            <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", s.cls)}><s.icon size={18} /></span>
            <div className="min-w-0">
              <p className="truncate text-xs text-slate-400">{s.label}</p>
              <p className="font-display text-xl font-bold text-slate-900 dark:text-white">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* controls */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input aria-label="Search batches and runs" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search batches and runs…"
            className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white" />
        </div>
        <div className="flex items-center gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            <option value="all">All statuses</option>
            {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <p className="hidden text-xs text-slate-400 md:block">{cfg.blurb}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : error ? (
        <div className="mb-2 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Couldn&apos;t load batches or runs — backend not reachable.
        </div>
      ) : visible.length === 0 ? (
        <EmptyState icon={Boxes}
          title={batches.length + singles.length === 0 ? `No ${cfg.label} runs yet` : "Nothing matches"}
          message={batches.length + singles.length === 0 ? "Run a single test or a batch from this flow and it will show up here." : "Try a different search or status filter."} />
      ) : (
        <div className="space-y-3">
          {visible.map((entry) => {
            if (entry.kind === "run") {
              return <SingleRunCard key={entry.key} flow={flow} run={entry.data} onOpen={onOpenRun} />;
            }
            const b = entry.data;
            const s = batchStats(b);
            const busy = reporting === b.id;
            return (
              // A <div> with an inner "Open batch" button, not one big <button>:
              // the Report control lived inside the card button as a
              // role="button" <span>, which is invalid nesting — screen readers
              // flatten it and Enter fired both handlers.
              <div key={entry.key}
                className="card group flex w-full flex-col gap-3 p-4 text-left transition-colors hover:border-brand-300 dark:hover:border-brand-500/40">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500/15 to-fuchsia-500/15 text-brand-600 dark:text-brand-300"><Boxes size={18} /></span>
                  <button
                    type="button"
                    onClick={() => onOpenBatch(b.id)}
                    className="min-w-0 flex-1 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
                  >
                    <p className="truncate font-semibold text-slate-900 group-hover:text-brand-600 dark:text-white dark:group-hover:text-brand-300">{batchName(b)}</p>
                    <p className="text-xs text-slate-400">{fmtWhen(b.created_at)}{b.elapsed_seconds ? ` · ${fmtDur(b.elapsed_seconds)}` : ""}{b.cost_usd ? ` · $${Number(b.cost_usd).toFixed(4)}` : ""}</p>
                  </button>
                  <StatusBadge status={b.status} />
                  <button
                    type="button"
                    onClick={(e) => downloadReport(e, b.id)}
                    disabled={busy}
                    title="Download this batch's Excel report"
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                  >
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Report
                  </button>
                  <button
                    type="button"
                    onClick={() => onOpenBatch(b.id)}
                    aria-label={`Open batch ${batchName(b)}`}
                    className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-slate-400 transition-transform group-hover:translate-x-0.5 hover:text-brand-600 dark:hover:text-brand-300"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>

                {/* counts + progress */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="rounded-md bg-emerald-100 px-2 py-0.5 font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">{s.passed} passed</span>
                    <span className="rounded-md bg-rose-100 px-2 py-0.5 font-medium text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">{s.failed} failed</span>
                    {s.inconclusive > 0 && <span className="rounded-md bg-amber-100 px-2 py-0.5 font-medium text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">{s.inconclusive} inconclusive</span>}
                    <span className="rounded-md bg-slate-100 px-2 py-0.5 font-medium text-slate-500 dark:bg-white/10 dark:text-slate-300">{s.total} total</span>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <div className="h-1.5 w-28 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                      <div className="h-full rounded-full bg-gradient-to-r from-brand-500 to-fuchsia-500" style={{ width: `${s.pct}%` }} />
                    </div>
                    <span className="text-xs tabular-nums text-slate-400">{s.pct}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BlockedCaseModal({ item, batch, onClose }) {
  const blocked = String(item.status || "").toLowerCase() === "blocked";
  const when = item.completed_at || item.created_at;
  const category = item.category || null;
  const testType = item.test_type || null;
  const reason = blockedReasonOfItem(item);
  const neverExecuted = !item.run_id;
  return (
    <Modal open onClose={onClose} title="Blocked case" size="xl">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          {blocked && <StatusBadge status="blocked" />}
          {batch?.label && <MetaChip icon={Boxes}>{String(batch.label).slice(0, 40)}</MetaChip>}
          {category && <MetaChip icon={ListChecks}>{category}</MetaChip>}
          {testType && <MetaChip icon={Puzzle}>{testType}</MetaChip>}
          {item.device && <MetaChip icon={Monitor}>{item.device}</MetaChip>}
          {when && <MetaChip icon={Clock}>{fmtWhen(when)}</MetaChip>}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Scenario</p>
          <p className="mt-1 text-sm font-medium text-slate-800 dark:text-white">
            {item.test_scenario || item.instruction || item.name || "—"}
          </p>
          {item.test_description && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{item.test_description}</p>
          )}
          {item.url && (
            <a href={item.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-brand-600 hover:underline dark:text-brand-400">
              <Globe size={12} /> {item.url} <ExternalLink size={11} />
            </a>
          )}
        </div>

        <Section label="Preconditions">
          <p className="whitespace-pre-line break-words text-sm text-slate-700 dark:text-slate-200">
            {item.preconditions || "No preconditions were recorded for this case."}
          </p>
        </Section>

        <Section label="Test steps">
          <p className="whitespace-pre-line break-words text-sm text-slate-700 dark:text-slate-200">
            {item.instruction || "No test steps were recorded with this case."}
          </p>
        </Section>

        <Section label="Test data">
          <p className="whitespace-pre-line break-words text-sm text-slate-700 dark:text-slate-200">
            {item.test_data || "No test data was recorded for this case."}
          </p>
        </Section>

        {item.expected_result && (
          <Section label="Expected result">
            <p className="whitespace-pre-line break-words text-sm text-slate-700 dark:text-slate-200">{item.expected_result}</p>
          </Section>
        )}

        <BlockedDiagnosis reason={reason} missingEvidence={neverExecuted} affected="The test never started, so nothing was executed." />

        {neverExecuted && (
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Execution trace</h4>
            <ol className="space-y-1.5 text-xs text-slate-600 dark:text-slate-300">
              <li><span className="mr-2 inline-grid h-4 w-4 place-items-center rounded-full bg-slate-200 text-[0.625rem] text-slate-600 dark:bg-white/10 dark:text-slate-300">1</span>Case queued in the batch</li>
              <li><span className="mr-2 inline-grid h-4 w-4 place-items-center rounded-full bg-slate-200 text-[0.625rem] text-slate-600 dark:bg-white/10 dark:text-slate-300">2</span>Prerequisite check</li>
              <li><span className="mr-2 inline-grid h-4 w-4 place-items-center rounded-full bg-violet-100 text-[0.625rem] font-semibold text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">3</span>BLOCKED — marked before execution</li>
              <li><span className="mr-2 inline-grid h-4 w-4 place-items-center rounded-full bg-slate-200 text-[0.625rem] text-slate-600 dark:bg-white/10 dark:text-slate-300">4</span>Execution stopped</li>
            </ol>
            <p className="mt-2 text-xs italic text-slate-400">This case never reached the runner, so the trace above is its recorded lifecycle, not executed test steps.</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ---- Batch detail (its cases) --------------------------------------------
function BatchDetail({ flow, batchId, onBack, onOpenCase, refreshNonce }) {
  const cfg = FLOWS[flow];
  const [batch, setBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [bugsOnly, setBugsOnly] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [blockedCase, setBlockedCase] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(false);
    Promise.all([cfg.batchApi(batchId), cfg.batchItemsApi(batchId)])
      .then(([b, its]) => { if (alive) { setBatch(b); setItems(normList(its)); } })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [cfg, batchId, refreshNonce]);

  const s = batch ? batchStats(batch) : { passed: 0, failed: 0, inconclusive: 0, total: items.length, pct: 0 };
  const bugCount = useMemo(() => items.filter(itemIsBug).length, [items]);
  const rows = useMemo(() => (bugsOnly ? items.filter(itemIsBug) : items), [items, bugsOnly]);
  // The items endpoint hard-caps at 500 per page and this view fetches one page,
  // so a bigger batch listed fewer cases than its own header counted — silently,
  // which reads as "these are all of them".
  const truncated = s.total > items.length;

  const openCase = (item) => {
    if (item.run_id) return onOpenCase(flow, { id: item.run_id });
    if (String(item.status || "").toLowerCase() === "blocked") return setBlockedCase(item);
  };

  const downloadReport = async () => {
    setReporting(true); setReportError(null);
    try {
      await cfg.reportApi(batchId);
    } catch (err) {
      setReportError((err && err.message) || "That report couldn't be downloaded.");
    } finally { setReporting(false); }
  };

  const columns = [
    {
      key: "name", header: "Case",
      render: (i) => (
        <div className="min-w-0 max-w-[16rem] sm:max-w-[26rem]">
          <p className="flex items-center gap-1.5">
            <span className="truncate font-medium text-slate-800 dark:text-white">{i.name || i.instruction || "—"}</span>
            {/* A bare "⚡" with no legend meant nothing to anyone reading the table. */}
            {i.is_replay && (
              <span title="Replay of an earlier run" className="inline-flex shrink-0 items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.625rem] font-medium text-slate-500 dark:bg-white/10 dark:text-slate-300">
                <Repeat size={10} /> replay
              </span>
            )}
          </p>
          {i.last_error && <p className="truncate text-xs text-rose-500 dark:text-rose-400" title={i.last_error}>{i.last_error}</p>}
        </div>
      ),
    },
    { key: "status", header: "Status", render: (i) => <StatusBadge status={i.status} /> },
    { key: "device", header: "Device", render: (i) => <span className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">{i.device || "—"}</span> },
    { key: "attempt", header: "Try", align: "right", render: (i) => <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{i.attempt ?? "—"}</span> },
    { key: "duration_seconds", header: "Duration", align: "right", sortable: true, render: (i) => <span className="whitespace-nowrap text-xs tabular-nums text-slate-500 dark:text-slate-400">{i.duration_seconds != null ? fmtDur(i.duration_seconds) : "—"}</span> },
    {
      key: "open", header: "", align: "right",
      render: (i) => {
        if (i.run_id) return <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-300">View <ChevronRight size={13} /></span>;
        if (String(i.status || "").toLowerCase() === "blocked") return <span className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 dark:text-violet-300">View <ChevronRight size={13} /></span>;
        return <span className="text-xs text-slate-300 dark:text-slate-600">—</span>;
      },
    },
  ];

  return (
    <div>
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-600 dark:text-slate-400">
        <ArrowLeft size={16} /> All batches
      </button>

      {/* batch header */}
      <div className="card mb-5 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-white shadow-glow"><Boxes size={20} /></span>
            <div className="min-w-0">
              <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white">{batch ? batchName(batch) : "Batch"}</h3>
              <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                {batch && <StatusBadge status={batch.status} />}
                {batch?.created_at && <span>{fmtWhen(batch.created_at)}</span>}
                {batch?.elapsed_seconds ? <span>· {fmtDur(batch.elapsed_seconds)}</span> : null}
                {batch?.cost_usd ? <span>· ${Number(batch.cost_usd).toFixed(4)}</span> : null}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={downloadReport} disabled={reporting}>
            {reporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Report
          </Button>
        </div>

        {/* counts */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: "Cases", value: s.total, icon: ListChecks, cls: "text-sky-600 bg-sky-500/10 dark:text-sky-300" },
            { label: "Passed", value: s.passed, icon: Check, cls: "text-emerald-600 bg-emerald-500/10 dark:text-emerald-300" },
            { label: "Failed", value: s.failed, icon: X, cls: "text-rose-600 bg-rose-500/10 dark:text-rose-300" },
            { label: "Inconclusive", value: s.inconclusive, icon: AlertCircle, cls: "text-amber-600 bg-amber-500/10 dark:text-amber-300" },
          ].map((c) => (
            <div key={c.label} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2.5 dark:border-white/5 dark:bg-white/5">
              <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", c.cls)}><c.icon size={15} /></span>
              <div className="min-w-0">
                <p className="truncate text-[0.6875rem] text-slate-400">{c.label}</p>
                <p className="text-base font-bold text-slate-900 dark:text-white">{c.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {reportError && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-2.5 text-sm text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          <AlertCircle size={15} className="shrink-0" /> {reportError}
        </div>
      )}

      {/* cases table */}
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 dark:border-white/10 dark:bg-white/5">
          {[["all", "All cases", ListChecks, items.length], ["bugs", "Bugs", Bug, bugCount]].map(([key, label, Icon, count]) => {
            const active = (key === "bugs") === bugsOnly;
            return (
              <button key={key} onClick={() => setBugsOnly(key === "bugs")}
                className={cn("inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors",
                  active ? "bg-brand-600 text-white shadow-soft" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5")}>
                <Icon size={15} /> {label} <span className="opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Couldn&apos;t load this batch&apos;s cases.
        </div>
      ) : (
        <>
        {truncated && (
          <div className="mb-3 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertCircle size={15} className="shrink-0" />
            Showing the first {items.length} of {s.total} cases. Open the batch report
            for the full, paged list.
          </div>
        )}
        <DataTable
          columns={columns}
          rows={rows}
          getRowId={getRowId}
          onRowClick={openCase}
          searchKeys={CASE_SEARCH_KEYS}
          filters={STATUS_FILTER}
          initialSort={{ key: "status", dir: "asc" }}
          maxHeight="36rem"
          dateKey="created_at"
          dateLabel="Run date"
          pageSize={15}
          emptyLabel={bugsOnly ? "No bugs in this batch." : "No cases in this batch."}
        />
        </>
      )}

      {blockedCase && (
        <BlockedCaseModal item={blockedCase} batch={batch} onClose={() => setBlockedCase(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared column builders for the Tester Dashboard flow (own runs, raw shape).
// ---------------------------------------------------------------------------
const dashWhenCol = {
  key: "created_at", header: "When", align: "right",
  render: (r) => <span className="whitespace-nowrap text-xs text-slate-400">{fmtWhen(r.created_at)}</span>,
};
const dashTestCol = {
  key: "instruction", header: "Test",
  render: (r) => (
    <div className="min-w-0 max-w-[15rem] sm:max-w-[24rem]">
      <p className="truncate font-medium text-slate-800 dark:text-white">{r.instruction || "—"}</p>
      <p className="truncate text-xs text-slate-400">{r.url}</p>
    </div>
  ),
};
const dashRunColumns = [
  dashTestCol,
  { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
  { key: "steps", header: "Steps", align: "right", render: (r) => <span className="whitespace-nowrap text-xs tabular-nums"><span className="text-emerald-600 dark:text-emerald-400">{r.steps_passed ?? 0}✓</span> <span className="text-rose-500">{r.steps_failed ?? 0}✗</span></span> },
  { key: "duration_seconds", header: "Duration", align: "right", render: (r) => <span className="whitespace-nowrap text-xs tabular-nums text-slate-500 dark:text-slate-400">{fmtDur(r.duration_seconds)}</span> },
  dashWhenCol,
  { key: "report", header: "Report", align: "right", render: (r) => <ReportButtons runId={r.id} /> },
];
const dashBugColumns = [
  {
    key: "title", header: "Bug",
    render: (b) => (
      <div className="min-w-0 max-w-[15rem] sm:max-w-[24rem]">
        <div className="flex items-center gap-2">
          <p className={cn("truncate font-medium", b.resolved ? "text-slate-500 line-through dark:text-slate-400" : "text-slate-800 dark:text-white")}>{b.title}</p>
          {b.resolved && (
            <span title="A later re-run of this test case passed — this bug appears fixed." className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[0.6875rem] font-semibold uppercase text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
              <CheckCircle2 size={10} /> Resolved
            </span>
          )}
        </div>
        <p className="truncate text-xs text-slate-400">{b.url}</p>
      </div>
    ),
  },
  { key: "severity", header: "Severity", render: (b) => <SeverityTag severity={b.severity} /> },
  dashWhenCol,
  { key: "report", header: "Report", align: "right", render: (b) => <ReportButtons runId={b.id} /> },
];

// Map a normalized bug-run into the flat row the bug table renders.
function toBugRow(b) {
  return {
    id: b.id,
    title: b.bug?.title || "",
    severity: b.bug?.severity || "",
    url: b.url,
    created_at: b.createdAtRaw || b.createdAt,
    resolved: !!b.resolved,
    resolvedLabel: b.resolved ? "Resolved" : "Open",
  };
}

// ---------------------------------------------------------------------------
// Collapsible section shell shared by the per-project and Unscoped cards.
// ---------------------------------------------------------------------------
// `expanded` / `onToggle` are OWNED BY THE PARENT. This used to keep its own
// expanded state, re-synced only when `defaultExpanded` changed — so the
// parent's Set (which drives the "Expand all" / "Collapse all" label) never
// learned about a section opened by hand. Open three by hand and the button
// still read "Expand all", then flipped its label without collapsing anything.
function DashSection({ icon: Icon, iconCls, title, badge, runs, bugs, expanded, onToggle, onOpen }) {
  const [view, setView] = useState("runs");

  const passed = runs.filter((r) => String(r.status).toLowerCase() === "passed").length;
  const failed = runs.filter((r) => isFail(r.status)).length;
  const openBugs = bugs.filter((b) => !b.resolved).length;

  const bugRows = useMemo(() => bugs.map(toBugRow), [bugs]);
  const rowsForView = view === "runs" ? runs : bugRows;
  const columnsForView = view === "runs" ? dashRunColumns : dashBugColumns;
  const openRow = (row) => onOpen("dashboard", row.id ? { id: row.id } : row);

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/5"
      >
        <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-xl shadow-soft", iconCls)}>
          <Icon size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-display text-base font-semibold text-slate-900 dark:text-white">{title}</h3>
            {badge}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <StatPill icon={ListChecks} value={runs.length} label="runs" tone="text-brand-500" />
            <StatPill icon={Check} value={passed} label="passed" tone="text-emerald-500" />
            <StatPill icon={X} value={failed} label="failed" tone="text-rose-500" />
            <StatPill icon={Bug} value={openBugs} label="open bugs" tone="text-amber-500" />
          </div>
        </div>
        <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition-transform", expanded && "rotate-90 text-brand-500")}>
          <ChevronRight size={16} />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden border-t border-slate-200 dark:border-white/10"
          >
            <div className="px-4 pb-4 pt-3">
              <div className="mb-3 inline-flex rounded-xl border border-slate-200 bg-white p-1 dark:border-white/10 dark:bg-white/5">
                {[["runs", "Runs", ListChecks, runs.length], ["bugs", "Bugs", Bug, bugRows.length]].map(([key, label, Icon2, count]) => (
                  <button key={key} onClick={() => setView(key)}
                    className={cn("inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                      view === key ? "bg-brand-600 text-white shadow-soft" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5")}>
                    <Icon2 size={13} /> {label} <span className="opacity-70">({count})</span>
                  </button>
                ))}
              </div>

              <DataTable
                columns={columnsForView}
                rows={rowsForView}
                getRowId={getRowId}
                searchKeys={DASH_SEARCH_KEYS}
                filters={view === "runs" ? STATUS_FILTER : BUG_FILTERS}
                onRowClick={openRow}
                initialSort={{ key: "created_at", dir: "desc" }}
                maxHeight="24rem"
                pageSize={8}
                emptyLabel={view === "runs" ? "No runs in this project yet." : "No bugs in this project."}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DashboardProjectGroup — loads the tester's own runs/bugs once, groups by
// project, and renders stat cards + collapsible sections (+ Unscoped tail).
// ---------------------------------------------------------------------------
function DashboardProjectGroup({ onOpen, refreshNonce }) {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsDemo, setProjectsDemo] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const runsState = useApiData(() => listTestRuns({ mine: true, limit: 200 }), DASH_DEMO_RUNS);
  const bugsState = useApiData(() => listBugs({ mine: true }), []);

  const loading = runsState.loading || bugsState.loading || projectsLoading;
  const demo = runsState.demo || bugsState.demo || projectsDemo;

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const data = await getProjects();
      setProjects(data || []);
      setProjectsDemo(false);
    } catch (e) {
      setProjects(DEMO_PROJECTS);
      setProjectsDemo(true);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects, refreshNonce]);

  useEffect(() => {
    if (projects.length === 0) return;
    setExpanded((prev) => {
      if (prev.size > 0) return prev;
      return new Set(projects.slice(0, 3).map((p) => p.id));
    });
  }, [projects]);

  const runsData = runsState.data;
  const bugsData = bugsState.data;

  const { runsByProject, unscopedRuns, totals } = useMemo(() => {
    const data = Array.isArray(runsData) ? runsData : [];
    const byProj = {};
    const unscoped = [];
    let passed = 0, failed = 0;
    for (const r of data) {
      if (r.project_id) (byProj[r.project_id] = byProj[r.project_id] || []).push(r);
      else unscoped.push(r);
      if (String(r.status).toLowerCase() === "passed") passed++;
      else if (isFail(r.status)) failed++;
    }
    return { runsByProject: byProj, unscopedRuns: unscoped, totals: { total: data.length, passed, failed } };
  }, [runsData]);

  const { bugsByProject, unscopedBugs } = useMemo(() => {
    const data = Array.isArray(bugsData) ? bugsData : [];
    const byProj = {};
    const unscoped = [];
    for (const b of data) {
      if (b.projectId) (byProj[b.projectId] = byProj[b.projectId] || []).push(b);
      else unscoped.push(b);
    }
    return { bugsByProject: byProj, unscopedBugs: unscoped };
  }, [bugsData]);

  // Toggle one section. The Set is the single source of truth for both the
  // sections and the Expand/Collapse-all label above them.
  const toggleSection = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const anyExpanded = expanded.size > 0;
  const hasUnscoped = unscopedRuns.length > 0 || unscopedBugs.length > 0;
  const bugsCount = Array.isArray(bugsData) ? bugsData.length : 0;

  if (loading && projects.length === 0 && (!Array.isArray(runsData) || runsData.length === 0)) {
    return <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>;
  }

  if (!projectsDemo && projects.length === 0 && !loading) {
    return (
      <div className="card flex flex-col items-center gap-3 p-10 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-white shadow-glow">
          <FolderKanban size={22} />
        </span>
        <h2 className="font-display text-lg font-semibold text-slate-900 dark:text-white">No projects yet</h2>
        <p className="max-w-md text-sm text-slate-500 dark:text-slate-400">
          Create a project first — runs and bugs are scoped per project so each project&apos;s results stay isolated.
        </p>
        {/* /projects is hard-blocked while cloud testing is paused, so the old
            "Create a project" CTA dropped you on a "temporarily unavailable"
            page. Projects are created from Generate Test Cases. */}
        <Button to="/dashboard/generate" size="sm"><Plus size={15} /> Create a project</Button>
      </div>
    );
  }

  return (
    <div key={refreshNonce}>
      {/* Stat strip */}
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: "Total runs", value: totals.total, icon: ListChecks, cls: "text-brand-600 bg-brand-500/10 dark:text-brand-300" },
          { label: "Passed", value: totals.passed, icon: Check, cls: "text-emerald-600 bg-emerald-500/10 dark:text-emerald-300" },
          { label: "Failures", value: totals.failed, icon: X, cls: "text-rose-600 bg-rose-500/10 dark:text-rose-300" },
          { label: "Bugs", value: bugsCount, icon: Bug, cls: "text-amber-600 bg-amber-500/10 dark:text-amber-300" },
        ].map((s) => (
          <div key={s.label} className="card flex items-center gap-3 p-4">
            <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", s.cls)}><s.icon size={18} /></span>
            <div className="min-w-0">
              <p className="truncate text-xs text-slate-400">{s.label}</p>
              <p className="font-display text-xl font-bold text-slate-900 dark:text-white">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Expand / Collapse control */}
      <div className="mb-4 flex items-center justify-between">
        <Button variant="ghost" size="sm"
          onClick={() => setExpanded(anyExpanded
            ? new Set()
            : new Set([...projects.map((p) => p.id), ...(hasUnscoped ? [UNSCOPED_KEY] : [])]))}
          disabled={projects.length === 0 && !hasUnscoped}>
          <ChevronDown size={16} className={cn("transition-transform", anyExpanded && "rotate-180")} />
          {anyExpanded ? "Collapse all" : "Expand all"}
        </Button>
      </div>

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      <div className="space-y-4">
        {projects.map((p) => (
          <DashSection
            key={p.id}
            icon={FolderKanban}
            iconCls="bg-gradient-to-br from-brand-500 to-fuchsia-500 text-white"
            title={p.name}
            badge={p.base_url && (
              <span className="inline-flex items-center gap-1 truncate rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.6875rem] text-slate-500 dark:bg-white/5 dark:text-slate-400">
                <Globe size={11} /> <span className="truncate max-w-[16rem]">{p.base_url}</span>
              </span>
            )}
            runs={runsByProject[p.id] || []}
            bugs={bugsByProject[p.id] || []}
            expanded={expanded.has(p.id)}
            onToggle={() => toggleSection(p.id)}
            onOpen={onOpen}
          />
        ))}
        {hasUnscoped && (
          <DashSection
            icon={Inbox}
            iconCls="bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300"
            title="Unscoped"
            badge={
              <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.6875rem] text-slate-500 dark:bg-white/5 dark:text-slate-400">
                runs with no project attached
              </span>
            }
            runs={unscopedRuns}
            bugs={unscopedBugs}
            expanded={expanded.has(UNSCOPED_KEY)}
            onToggle={() => toggleSection(UNSCOPED_KEY)}
            onOpen={onOpen}
          />
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Runs & Bugs — flow-tabbed layout (Tester Dashboard / Extension / Exe Agent),
// mirroring the company Admin view but scoped to the tester's own runs.
// ===========================================================================
export default function RunsBugs() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  // This page is shared between the tester shell (/dashboard/runs) and the PM
  // shell (/pm/runs). The backend treats project_manager, admin and superadmin
  // as company-wide (core/access.py: COMPANY_WIDE_ROLES), so `mine` must be
  // true ONLY for scoped roles — a PM requesting "only my runs" sees nothing,
  // because the PM never runs tests. Any unknown/absent role keeps the safe
  // tester default so nothing shows beyond the caller's own work.
  const mine = user?.role ? !["project_manager", "admin", "superadmin"].includes(user.role) : true;
  // Default to the first ENABLED flow; a disabled source (e.g. paused cloud runs)
  // can't be selected via the tab or a ?flow= deep link.
  const firstEnabledFlow = FLOW_ORDER.find((k) => !FLOWS[k].disabled) || FLOW_ORDER[0];
  const requestedFlow = params.get("flow");
  const initialFlow =
    FLOW_ORDER.includes(requestedFlow) && !FLOWS[requestedFlow].disabled
      ? requestedFlow
      : firstEnabledFlow;
  const [flow, setFlow] = useState(initialFlow);
  // Keep the tab in step with the URL AFTER mount too. `initialFlow` is only read
  // once, so arriving from a ?flow= link while already on this page — or using
  // Back — left the previously selected tab showing under a URL naming another.
  useEffect(() => {
    const requested = params.get("flow");
    if (!requested) return;
    if (!FLOW_ORDER.includes(requested) || FLOWS[requested].disabled) return;
    setFlow(requested);
  }, [params]);
  const [detail, setDetail] = useState(null); // { flow, row }
  const [nonce, setNonce] = useState(0); // force-remount on refresh
  const [rerunning, setRerunning] = useState(false);

  // Feedback goes through the app-wide toast store, not a page-local stack — the
  // app had five toast implementations in four different corners of the screen,
  // and the ones with a local setTimeout leaked state updates after unmount.
  // Refresh remounts the panels (that IS the reload), so there is no promise to
  // await — but a silent remount that returns the same numbers is indistinguishable
  // from a dead button, which is exactly how it was reported. Spin briefly, then say
  // it happened.
  const [refreshing, setRefreshing] = useState(false);
  // Remounting the panels IS the reload. `reload()` does only that, silently —
  // for the places that reload as a side effect of something else (a re-run
  // landing). `refresh()` is the BUTTON: it also spins and says it happened.
  // They used to be one function, so starting a re-run fired "Runs & bugs
  // refreshed." twice on top of "Re-run started" — three toasts for one action.
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  function refresh() {
    if (refreshing) return;
    setRefreshing(true);
    reload();
    window.setTimeout(() => {
      setRefreshing(false);
      toastSuccess("Runs & bugs refreshed.");
    }, 600);
  }
  const selectFlow = (key) => {
    if (FLOWS[key].disabled) return; // paused source — not selectable
    setFlow(key);
    setParams(key === firstEnabledFlow ? {} : { flow: key }, { replace: true });
  };

  // Re-run a dashboard-flow run — forwards projectId so the new run lands in the
  // same project instead of becoming an unscoped run.
  async function rerunSelected(run) {
    if (!run || rerunning) return;
    if (!run.url || !run.instruction) {
      toastError("This run is missing a URL or instruction and can't be re-run.");
      return;
    }
    setRerunning(true);
    try {
      const res = await startRun(run.url, run.instruction, { projectId: run.projectId || undefined });
      const newRunId = res?.run_id;
      setDetail(null);
      toastSuccess("Re-run started — it'll update below when it finishes.");
      reload(); // show the new pending run immediately, not only after it settles
      if (newRunId) {
        await pollUntil(() => getRun(newRunId), {
          interval: 2000,
          isDone: (r) => {
            const s = String(r?.status || "").toLowerCase();
            return isTerminal(s) || s === "cancelled";
          },
        });
      }
      reload();
    } catch (e) {
      toastError(e?.message || "Failed to queue the re-run.");
    } finally {
      setRerunning(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Runs & Bugs"
        subtitle="Every test run and bug across your three testing flows."
        actions={
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
            <RefreshCw size={16} className={cn(refreshing && "animate-spin")} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        }
      />

      {/* Flow tabs */}
      <div className="mb-6 flex flex-wrap gap-2">
        {FLOW_ORDER.map((key) => {
          const f = FLOWS[key];
          const active = flow === key;
          const disabled = !!f.disabled;
          return (
            <button key={key} onClick={() => selectFlow(key)} disabled={disabled}
              title={disabled ? "Temporarily disabled" : undefined}
              aria-disabled={disabled}
              className={cn("group inline-flex items-center gap-2.5 rounded-xl border px-4 py-2.5 text-left transition-colors",
                disabled
                  ? "cursor-not-allowed border-slate-200 opacity-60 dark:border-white/10"
                  : active
                  ? "border-brand-500 bg-brand-50/70 dark:border-brand-500/40 dark:bg-brand-500/10"
                  : "border-slate-200 hover:border-slate-300 dark:border-white/10 dark:hover:border-white/20")}>
              <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors",
                disabled ? "bg-slate-100 text-slate-300 dark:bg-white/5 dark:text-slate-600"
                : active ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-500 group-hover:text-brand-600 dark:bg-white/10 dark:text-slate-300")}>
                <f.icon size={18} />
              </span>
              <span className="min-w-0">
                <span className={cn("flex items-center gap-2 text-sm font-semibold",
                  disabled ? "text-slate-400 dark:text-slate-500" : active ? "text-brand-700 dark:text-brand-200" : "text-slate-700 dark:text-slate-200")}>
                  {f.label}
                  {disabled && <span className="text-[0.625rem] font-semibold lowercase text-rose-500 dark:text-rose-400">temporarily disabled</span>}
                </span>
                <span className="hidden text-xs text-slate-400 sm:block">{f.blurb}</span>
              </span>
            </button>
          );
        })}
      </div>

      {flow === "dashboard"
        ? <DashboardProjectGroup key={`dash-${nonce}`} onOpen={(fl, row) => setDetail({ flow: fl, row })} refreshNonce={nonce} />
        : <HeadedFlowPanel key={flow} flow={flow} mine={mine} refreshNonce={nonce}
                           onOpen={(fl, row) => setDetail({ flow: fl, row })} />
      }

      {detail && (
        <DetailModal
          flow={detail.flow}
          row={detail.row}
          onClose={() => setDetail(null)}
          onRerun={rerunSelected}
          rerunning={rerunning}
        />
      )}

    </>
  );
}
